import { ChevronDown, Info } from "lucide-react";
import React, { ComponentProps, ReactNode } from "react";

import type { Optional } from "@intelligems/ig-types";
import {
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TypographyProps,
  useTheme,
} from "@mui/material";

import { IgIconComponent } from "./IgIconComponent";
import { IgTooltip } from "./IgTooltip";
import { IgTypography } from "./IgTypography";
import { IgCustomTheme } from "./theme";

type IgSelectProps<Value = unknown> = Omit<
  Optional<SelectProps<Value>, "variant">,
  "onChange"
> & {
  placeholder?: string;
  onChange: IgSelectEventHandler<Value>;
  label?: string;
  labelColor?: string;
  orientation?: "horizontal" | "vertical";
  helperText?: string;
  tooltipMessage?: string;
  labelProps?: TypographyProps;
  disabledTooltipMessage?: ReactNode;
};

type IgSelectEvent<Value = unknown> = {
  target: { value: Value; name: string };
};
export type IgSelectEventHandler<Value> = (ev: IgSelectEvent<Value>) => void;
type SelectProps<Value> = ComponentProps<typeof Select<Value>>;

export const IgSelect = <Value = unknown,>(props: IgSelectProps<Value>) => {
  const {
    children,
    placeholder,
    sx,
    variant,
    onChange,
    label,
    orientation,
    helperText,
    tooltipMessage,
    labelProps,
    fullWidth,
    disabledTooltipMessage,
    required,
    ...rest
  } = props;

  const isHorizontal = orientation === "horizontal";
  const theme = useTheme<IgCustomTheme>();

  return (
    <Grid
      container
      direction={isHorizontal ? "row" : "column"}
      columnSpacing={isHorizontal ? 1 : 0}
      rowSpacing={isHorizontal ? 0 : 1}
      wrap={"nowrap"}
      sx={{
        "& .lucide-chevron-down": {
          width: "16px",
          height: "16px",
          color: theme.igIconColors.light,
        },
      }}
    >
      {label && (
        <Grid
          item
          container
          direction={"row"}
          alignItems={"center"}
          spacing={0.5}
          xs={isHorizontal ? "auto" : 12}
          mt={
            isHorizontal && !helperText
              ? -1
              : helperText && isHorizontal
                ? `-${theme.spacing(7)}`
                : 0
          }
        >
          <Grid item>
            <IgTypography {...labelProps}>
              {label}

              {required ? (
                <IgTypography
                  {...labelProps}
                  color={theme.palette.text.tonal.error}
                >
                  {" "}
                  *
                </IgTypography>
              ) : (
                ""
              )}
            </IgTypography>
          </Grid>
          {tooltipMessage && (
            <Grid item>
              <IgTooltip title={tooltipMessage}>
                <IconButton size={"small"}>
                  <Info size={18} />
                </IconButton>
              </IgTooltip>
            </Grid>
          )}
        </Grid>
      )}
      <Grid
        sx={{ paddingTop: "0px !important" }}
        item
        xs={isHorizontal ? "auto" : 12}
      >
        <FormControl fullWidth={fullWidth}>
          <IgTooltip title={disabledTooltipMessage}>
            <Select<Value>
              sx={{
                ...(!isHorizontal &&
                  label && {
                    marginTop: 2,
                  }),
                ...sx,
              }}
              required={required}
              onChange={onChange as unknown as SelectProps<Value>["onChange"]}
              variant={variant}
              MenuProps={{ disablePortal: true }}
              IconComponent={IgIconComponent}
              fullWidth={fullWidth}
              {...rest}
            >
              {placeholder ? (
                <MenuItem sx={{ display: "none" }} value={"ig-empty"}>
                  <IgTypography color={theme.palette.text.placeholder}>
                    {placeholder}
                  </IgTypography>
                </MenuItem>
              ) : null}
              {children}
            </Select>
          </IgTooltip>
          {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
        </FormControl>
      </Grid>
    </Grid>
  );
};
